import { useState } from "react";
import { Mail } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message);
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      toast.error("送信に失敗しました。もう一度お試しください。");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      data-testid="contact-section"
      className="py-20 md:py-32 px-6 md:px-12 bg-slate-50"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        {/* Left side - Context */}
        <div data-testid="contact-intro" className="space-y-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary">
              お問い合わせ
            </h2>
          </div>
          
          <p className="text-base leading-relaxed text-slate-600">
            eBayでのビジネス展開に関するご質問や、コンサルティングサービスの詳細について、お気軽にお問い合わせください。
          </p>
          
          <p className="text-base leading-relaxed text-slate-600">
            経験豊富な専門スタッフが、お客様のビジネスに最適なソリューションをご提案いたします。
          </p>

          <div className="pt-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-amber-600 flex-shrink-0 mt-0.5"></div>
              <p className="text-sm text-slate-600">
                24時間以内に返信いたします
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-amber-600 flex-shrink-0 mt-0.5"></div>
              <p className="text-sm text-slate-600">
                無料相談も承っております
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Form */}
        <div 
          data-testid="contact-form"
          className="bg-white rounded-2xl p-8 md:p-10 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-2 block">
                お名前 *
              </Label>
              <Input
                id="name"
                name="name"
                data-testid="contact-name-input"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary"
                placeholder="山田 太郎"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-2 block">
                メールアドレス *
              </Label>
              <Input
                id="email"
                name="email"
                data-testid="contact-email-input"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <Label htmlFor="subject" className="text-sm font-medium text-slate-700 mb-2 block">
                件名 *
              </Label>
              <Input
                id="subject"
                name="subject"
                data-testid="contact-subject-input"
                type="text"
                required
                value={formData.subject}
                onChange={handleChange}
                className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary"
                placeholder="お問い合わせ内容"
              />
            </div>

            <div>
              <Label htmlFor="message" className="text-sm font-medium text-slate-700 mb-2 block">
                メッセージ *
              </Label>
              <Textarea
                id="message"
                name="message"
                data-testid="contact-message-input"
                required
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary resize-none"
                placeholder="ご質問やご相談内容をご記入ください"
              />
            </div>

            <Button
              type="submit"
              data-testid="contact-submit-button"
              disabled={isSubmitting}
              className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-base font-medium transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "送信中..." : "送信する"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
